import React, { useEffect } from 'react';
import { Switch, Route, useLocation, useHistory } from 'react-router-dom';

import PrivateRoute from 'components/PrivateRoute';
import LoginPage from 'pages/Login/LoginPage';
import NotFoundPage from 'components/NotFoundPage';
import CouponsPage from 'pages/Coupons/CouponsPage';
import DiscountSettingsPage from 'pages/DiscountSettings/DiscountSettingsPage';
import DiscountRulesPage from 'pages/DiscountRules/DiscountRulesPage';
import HomePage from 'pages/Home/HomePage';
import Version from 'pages/Version';
import KybReviewPage from 'pages/Kyb/KybReviewPage';
import ChangePasswordPage from 'pages/InternalToolsUser/ChangePasswordPage';
import NpwpReviewPage from 'pages/Npwp/NpwpReviewPage';
import ActivationPage from 'pages/Activation/ActivationPage';
import MokaConfigPage from 'pages/MokaConfig/MokaConfigPage';
import ResetPhonePage from 'pages/ResetPhone/ResetPhonePage';
import { loadSessionPermission } from 'state/session/actions';
import { useDispatch, useSelector } from 'react-redux';
import BulkOutletUpdateExpiration from 'pages/BulkOutletUpdateExpiration/BulkOutletUpdateExpiration';
import { usePrevious } from 'utils/reactHooksUtils';
import ProcessStatus from 'constants/bulkAction/processStatus';
import * as bulkOutletUpdateActions from 'state/bulkOutletUpdateExpiration/actions';
import AccessPage from 'pages/Access/AccessPage';
import UserMappingPage from 'pages/UserMapping/UserMappingPage';
import BusinessPage from 'pages/Business/BusinessPage';
import LogoutPage from 'pages/Logout/LogoutPage';
import Layout from 'components/Layout/Layout';
import BulkDeleteBusiness from 'pages/BulkDeleteBusiness/BulkDeleteBusiness';
import ChangeUserEmailPage from 'pages/ChangeUserEmail/ChangeUserEmailPage';
import BillingPackagePage from 'pages/BillingPackage/BillingPackagePage';
import AdditionalServicePage from 'pages/AdditionalService/AdditionalServicePage';
import HardwareCataloguePage from 'pages/HardwareCatalogue/HardwareCataloguePage';
import RestoreDeletedBusiness from 'pages/RestoreDeletedBusiness/RestoreDeletedBusiness';
import KycContactListPage from 'pages/KycContactList/KycContactListPage';

const privatePaths = [
  '/',
  '/business',
  '/delete-business',
  '/coupons',
  '/discounts',
  '/discount-rules',
  '/kyb',
  '/npwp',
  '/config',
  '/activation',
  '/change-password',
  '/reset-phone-number',
  '/change-user-email',
  '/outlet-bulk-update-expiration',
  '/access',
  '/user-mapping',
  '/billing-packages',
  '/additional-services',
  '/hardware-catalogues',
  '/restore-deleted-business',
  '/kyc',
];

function App() {
  const dispatch = useDispatch();
  const isLoggedIn = !!useSelector(state => state.session.currentUser);
  const pages = useSelector(state => state.session.permission.pages);
  const history = useHistory();
  const { pathname } = useLocation();
  useEffect(() => {
    if (isLoggedIn && !(pathname === '/logout')) {
      dispatch(loadSessionPermission());
    } else if (privatePaths.includes(pathname)) {
      history.push('/login', { previousPath: pathname });
    }
  }, [pathname, isLoggedIn, dispatch, history]);

  const previousPath = usePrevious(pathname);
  const bulkOutletUpdateCurrentStatus = useSelector(
    state => state.bulkOutletUpdateExpiration.process.status
  );
  useEffect(() => {
    if (
      pathname !== previousPath &&
      previousPath === '/outlet-bulk-update-expiration'
    ) {
      if (bulkOutletUpdateCurrentStatus === ProcessStatus.InProgress) {
        dispatch(bulkOutletUpdateActions.abortProcess());
      }
      dispatch(bulkOutletUpdateActions.setIsProcessing(false));
    }
  }, [bulkOutletUpdateCurrentStatus, dispatch, pathname, previousPath]);

  return (
    <Layout>
      <Switch>
        <Route path="/version" component={Version} />
        <Route path="/logout" component={LogoutPage} />
        <Route path="/login" component={LoginPage} />
        {!privatePaths.includes(pathname) && <Route component={NotFoundPage} />}
        {pages.size !== 0 && (
          <Switch>
            <PrivateRoute
              path="/"
              exact
              component={HomePage}
              excludePagePermissionCheck
            />
            <PrivateRoute path="/coupons" component={CouponsPage} />
            <PrivateRoute path="/discounts" component={DiscountSettingsPage} />
            <PrivateRoute
              path="/discount-rules"
              component={DiscountRulesPage}
            />
            <PrivateRoute path="/kyb" component={KybReviewPage} />
            <PrivateRoute path="/npwp" component={NpwpReviewPage} />
            <PrivateRoute path="/config" component={MokaConfigPage} />
            <PrivateRoute path="/activation" component={ActivationPage} />
            <PrivateRoute
              path="/change-password"
              component={ChangePasswordPage}
              excludePagePermissionCheck
            />
            <PrivateRoute
              path="/reset-phone-number"
              component={ResetPhonePage}
              excludePagePermissionCheck
            />
            <PrivateRoute
              path="/outlet-bulk-update-expiration"
              component={BulkOutletUpdateExpiration}
            />
            <PrivateRoute
              path="/change-user-email"
              component={ChangeUserEmailPage}
            />
            <PrivateRoute path="/access" component={AccessPage} />
            <PrivateRoute path="/user-mapping" component={UserMappingPage} />
            <PrivateRoute path="/business" component={BusinessPage} />
            <PrivateRoute
              path="/delete-business"
              component={BulkDeleteBusiness}
            />
            <PrivateRoute
              path="/billing-packages"
              component={BillingPackagePage}
            />
            <PrivateRoute
              path="/additional-services"
              component={AdditionalServicePage}
            />
            <PrivateRoute
              path="/hardware-catalogues"
              component={HardwareCataloguePage}
            />
            <PrivateRoute
              path="/restore-deleted-business"
              component={RestoreDeletedBusiness}
            />
            <PrivateRoute
              path="/kyc"
              component={KycContactListPage}
              excludePagePermissionCheck
            />
          </Switch>
        )}
      </Switch>
    </Layout>
  );
}

export default App;
